// ITER287 — Presentational category section, reused by GuestMenu.
// Owns no state. Registers itself with the IntersectionObserver via a
// forwarded ref so the parent can track the active category on scroll.
//
// Sub-categories: items are grouped by their `subcategory` field when
// any items in the section define one. Items with no subcategory render
// under an "unnamed" head-less bucket first, preserving parser order.
import React, { useMemo } from 'react';
import { MenuItemCard } from './MenuItemCard';

function groupBySubcategory(items) {
  const buckets = new Map();
  const order = [];
  for (const it of items) {
    const key = (it.subcategory || '').trim();
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key).push(it);
  }
  // Ensure the "no subcategory" bucket renders first if it exists.
  order.sort((a, b) => (a === '' ? -1 : b === '' ? 1 : 0));
  return order.map((key) => ({ key, items: buckets.get(key) }));
}

export function CategorySection({ category, theme, refCb, VegDot }) {
  // Only show items that are available OR that are missing the flag
  // (older parsed data may not carry `available`). Sold-out items are
  // filtered from the guest experience.
  const items = (category.items || []).filter(
    (it) => it.available !== false
  );
  const groups = useMemo(() => groupBySubcategory(items), [items]);

  if (items.length === 0) return null;

  return (
    <section
      ref={refCb}
      data-testid={`category-${category.id}`}
      data-category-id={category.id}
      className="scroll-mt-40"
    >
      <div
        className="text-xs uppercase tracking-[0.25em] font-semibold pb-1 border-b"
        style={{
          color: theme.secondary,
          borderColor: `${theme.secondary}44`,
        }}
      >
        {category.name}
      </div>
      {category.description && (
        <div className="text-xs text-gray-500 mt-1 italic">
          {category.description}
        </div>
      )}
      {groups.map((g, gi) => (
        <div key={gi}>
          {g.key && (
            <div
              className="text-[11px] uppercase tracking-widest font-medium mt-4 mb-1 opacity-70"
              style={{ color: theme.primary }}
              data-testid={`subcategory-${category.id}-${gi}`}
            >
              {g.key}
            </div>
          )}
          <ul
            className="divide-y"
            style={{ borderColor: `${theme.secondary}22` }}
          >
            {g.items.map((it) => (
              <MenuItemCard
                key={it.id}
                item={it}
                theme={theme}
                VegDot={VegDot}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export default CategorySection;
